/**
 * Turning the survey payloads into what the screens show.
 *
 * Kept apart from the components because every one of these is a decision about meaning — what a
 * `null` response rate is called, what a delivery window with no bounds says, where a grid row's
 * average sits on its scale — and those are worth testing.
 *
 * @module Pages/BUs/Surveys
 */

import type { IGLIcon } from '@group-link-one/gl-icons';
import type {
  DelayAnchor,
  DelayUnit,
  OptInState,
  Survey,
  SurveyApprovalStatus,
  SurveyEligibilityScope,
  SurveyLayout,
  SurveyQuestionResult,
  SurveyQuestionType,
  SurveyResults,
  SurveyResultsOptIn,
  SurveyTranslation,
  SurveyTrigger,
} from '../Types/Surveys.types';
import { ADMIN_LABEL_LANGUAGE } from '../Types/Surveys.types';
import type { Status } from '@/Shared/components/StatusTag/StatusTag';

export const formatCount = (count: number): string => count.toLocaleString('pt-BR');

/** `0.351` → `35,1%`. */
export const formatPercent = (value: number): string =>
  `${(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export const formatAverage = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/** `161200` → `2 min 41 s`. Milliseconds are unreadable and seconds alone lose the scale. */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '—';

  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds} s`;

  return `${minutes} min ${String(seconds).padStart(2, '0')} s`;
};

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatShortDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/**
 * The delivery window, in words.
 *
 * A missing bound is "sem limite", never a blank: a window open at one end is a deliberate
 * setting, and an empty cell reads as "not configured yet".
 */
export const formatWindow = (opensAt: string | null, closesAt: string | null): string => {
  if (!opensAt && !closesAt) return 'Sem janela — vale enquanto estiver ativa';
  if (opensAt && !closesAt) return `De ${formatShortDate(opensAt)}, sem fim definido`;
  if (!opensAt && closesAt) return `Até ${formatShortDate(closesAt)}`;

  return `${formatShortDate(opensAt as string)} → ${formatShortDate(closesAt as string)}`;
};

/** Whether the survey is inside its own window right now — the card's "no ar" badge depends on it. */
export const isWithinWindow = (survey: Survey, now = new Date()): boolean => {
  if (survey.opensAt && new Date(survey.opensAt) > now) return false;
  if (survey.closesAt && new Date(survey.closesAt) < now) return false;

  return true;
};

export const APPROVAL_LABELS: Record<SurveyApprovalStatus, string> = {
  DRAFT: 'Rascunho',
  PENDING_APPROVAL: 'Aguardando aprovação',
  APPROVED: 'Aprovada',
  REJECTED: 'Rejeitada',
};

/**
 * The title to print on screen, in the panel's language.
 *
 * **The base columns are English by design** (`06-i18n.md`), and this panel is entirely pt-BR — so
 * a listing that renders `survey.title` raw shows "Thanks for trying the new menu" over a card
 * whose every other word is Portuguese.
 *
 * The rule is the BFF's own, and the same one delivery uses: **a translation row that exists wins;
 * only its total absence falls back to the base column.** `?language=` defaults to `pt` on every
 * admin endpoint for exactly this reason, and the listing is the one place that was reading around
 * it, because `GET /v1/surveys` hands the translations over and lets the client choose.
 *
 * The language is the panel's own `ADMIN_LABEL_LANGUAGE` — the same constant the results calls send
 * as `?language=`, so the listing and the aggregates can never end up resolving to two different
 * languages. `null` there (the escape hatch for a BFF that rejects the parameter) means the base
 * column everywhere, here included.
 *
 * The `||` rather than `??` is deliberate and mirrors `translation?.name || feature.name` in the
 * BFF: a row that exists always carries a title, and if one ever arrives empty, an English title is
 * better than a blank card.
 */
export const surveyTitle = (
  survey: Pick<Survey, 'title'> & { translations?: SurveyTranslation[] },
  language: string | null = ADMIN_LABEL_LANGUAGE,
): string =>
  (language
    ? survey.translations?.find(translation => translation.language === language)?.title
    : undefined) || survey.title;

export const APPROVAL_TONES: Record<SurveyApprovalStatus, Status> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'pending',
  APPROVED: 'success',
  REJECTED: 'danger',
};

export const TRIGGER_LABELS: Record<SurveyTrigger, string> = {
  AUTO: 'Abre sozinha',
  ON_DEMAND: 'Sob demanda',
  ON_OPT_OUT: 'Ao desativar',
};

/**
 * One icon per trigger, chosen to be read **without** a legend — and never instead of the label.
 *
 * The three moments are what has to come across at a glance: the survey that arrives on its own, the
 * one the person goes and asks for, and the one that only exists at the instant somebody leaves.
 *
 * - `AUTO` — a bell mid-ring: it comes to you, uninvited.
 * - `ON_DEMAND` — a click: nothing happens until the person acts.
 * - `ON_OPT_OUT` — the exit door: it is the leaving itself, not a survey scheduled for later.
 *
 * **The icon accompanies the text, it does not replace it.** Alone, any of these is a riddle to
 * whoever opens this screen for the first time, and this screen is mostly opened for the first
 * time.
 */
export const TRIGGER_ICONS: Record<SurveyTrigger, IGLIcon['variant']> = {
  AUTO: 'Notifications.BellRinging',
  ON_DEMAND: 'Misc.MouseRightClick',
  ON_OPT_OUT: 'Arrows.SignOut',
};

/**
 * What choosing each trigger actually commits to, written where it is chosen.
 *
 * `ON_OPT_OUT` is the one that cannot be inferred from its name: it is not "opens when the feature
 * is turned off" in the sense of joining the queue then — it never joins the queue at all (R34).
 */
export const TRIGGER_HINTS: Record<SurveyTrigger, string> = {
  AUTO: 'Aparece sozinha para quem for elegível, respeitando prioridade e intervalo entre pesquisas.',
  ON_DEMAND: 'Fica disponível para quem quiser abrir; não aparece sozinha.',
  ON_OPT_OUT:
    'É o questionário de saída de uma funcionalidade opt-in: aparece SÓ dentro do modal de desativar, nunca sozinha. Fica fora da entrega automática e da lista sob demanda, e o único caminho até ela é a funcionalidade vinculada.',
};

/** Whether this survey is the exit questionnaire of an opt-in feature. */
export const isExitSurvey = (survey: Pick<Survey, 'trigger'>): boolean =>
  survey.trigger === 'ON_OPT_OUT';

/**
 * Why an exit questionnaire is, or is not, being asked.
 *
 * It mirrors `findExitSurveyIdsByFeatureKey` in the BFF one condition at a time, because the whole
 * failure mode of R35 is that **none of these produce an error anywhere**: a draft questionnaire is
 * simply not served, the bootstrap answers `exitSurveyId: null`, the modal confirms without asking,
 * and nothing on any screen says a question was written and is reaching nobody. The panel is the
 * only defence, so it has to be able to name the reason rather than print a status tag.
 *
 * `ORPHAN` is the one that is not about time or moderation: a questionnaire naming no feature has
 * no route to a person at all, since `ON_OPT_OUT` is out of the automatic queue (R34).
 */
export type ExitSurveyState =
  | 'READY'
  | 'ORPHAN'
  | 'DELETED'
  | 'NOT_APPROVED'
  | 'INACTIVE'
  | 'OUT_OF_WINDOW';

export const exitSurveyState = (survey: Survey, now = new Date()): ExitSurveyState => {
  if (survey.deletedAt) return 'DELETED';
  if (!survey.requiresOptInFeature) return 'ORPHAN';
  if (survey.approvalStatus !== 'APPROVED') return 'NOT_APPROVED';
  if (!survey.isActive) return 'INACTIVE';
  if (!isWithinWindow(survey, now)) return 'OUT_OF_WINDOW';

  return 'READY';
};

export const EXIT_STATE_LABELS: Record<ExitSurveyState, string> = {
  READY: 'Em uso',
  ORPHAN: 'Sem funcionalidade',
  DELETED: 'Excluída',
  NOT_APPROVED: 'Não aprovada',
  INACTIVE: 'Desativada',
  OUT_OF_WINDOW: 'Fora da janela',
};

export const EXIT_STATE_TONES: Record<ExitSurveyState, Status> = {
  READY: 'info',
  ORPHAN: 'danger',
  DELETED: 'danger',
  NOT_APPROVED: 'pending',
  INACTIVE: 'neutral',
  OUT_OF_WINDOW: 'neutral',
};

/**
 * What a state that is not `READY` costs, in one sentence, from the point of view of the feature.
 *
 * Always phrased as what stops being asked, never as what the row's status is: "Rascunho" is
 * already on the card, and repeating it would add a tag where a consequence was needed.
 */
export const EXIT_STATE_CONSEQUENCES: Record<ExitSurveyState, string> = {
  READY: '',
  ORPHAN:
    'Não aponta para funcionalidade nenhuma, e um questionário de saída só é alcançado pela funcionalidade — então ninguém responde a este, nunca.',
  DELETED: 'Excluída: não é entregue e só volta a existir se for restaurada.',
  NOT_APPROVED:
    'Ainda não aprovada, e rascunho não é servido: quem desativar a funcionalidade vê só a confirmação, sem pergunta e sem erro.',
  INACTIVE: 'Desativada: para de ser entregue na hora, mesmo aprovada.',
  OUT_OF_WINDOW: 'Fora da janela de coleta: nesta data ela não é perguntada a ninguém.',
};

/**
 * Which questionnaire a person leaving the feature actually gets.
 *
 * The same rule as the BFF's: among the ones that would be served, the **newest created** wins.
 *
 * Since R45 a feature has at most one live exit questionnaire — the BFF refuses the second and a
 * partial unique index makes it impossible — so the tie-break is now a defence against rows written
 * before that rule, not the normal path. It still takes a list and still returns `null`, because
 * **"exists" and "is served" are different questions**: one questionnaire in draft, switched off or
 * outside its window is `[survey]` with `null` served, which is precisely the silent failure of R35
 * the screen has to name out loud. Asserting there is only one would answer neither.
 */
export const pickServedExitSurvey = (surveys: Survey[], now = new Date()): Survey | null => {
  const ready = surveys.filter(survey => exitSurveyState(survey, now) === 'READY');

  if (ready.length === 0) return null;

  return [...ready].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];
};

export const LAYOUT_LABELS: Record<SurveyLayout, string> = {
  SINGLE_PAGE: 'Página única',
  ONE_PER_STEP: 'Um passo por tela',
};

export const QUESTION_TYPE_LABELS: Record<SurveyQuestionType, string> = {
  SHORT_TEXT: 'Texto curto',
  LONG_TEXT: 'Texto longo',
  SINGLE_CHOICE: 'Escolha única',
  MULTI_CHOICE: 'Múltipla escolha',
  RATING: 'Nota',
  NPS: 'NPS',
  BOOLEAN: 'Sim / não',
  MATRIX: 'Grade',
};

export const QUESTION_TYPE_HINTS: Record<SurveyQuestionType, string> = {
  SHORT_TEXT: 'Uma linha. Não entra em média nenhuma — o texto aparece na aba de comentários.',
  LONG_TEXT: 'Parágrafo. O conteúdo vive na aba de comentários, nunca nos agregados.',
  SINGLE_CHOICE: 'Uma opção entre várias.',
  MULTI_CHOICE: 'Várias opções. A soma dos percentuais passa de 100%, e é isso mesmo.',
  RATING: 'Escala numérica com rótulos nas pontas.',
  NPS: 'Escala fixa de 0 a 10, com promotores, neutros e detratores.',
  BOOLEAN: 'Sim ou não, com os rótulos que você escrever.',
  MATRIX: 'Uma escala só, avaliada linha a linha. Cada linha vira uma coluna no CSV.',
};

/**
 * Whom the numbers were measured against.
 *
 * `EVERYONE` means the survey has no opt-in condition, so it reaches every platform user of the
 * tenant — a roster that lives in the org backend, not in the BFF. The screen writes the audience
 * out in words instead of printing the `null` as a zero, which would read as "nobody was
 * eligible, and 214 people answered anyway".
 */
export const eligibleLabel = (
  scope: SurveyEligibilityScope,
  totalEligible: number | null,
): string => {
  if (scope === 'EVERYONE' || totalEligible === null) return 'Todos os usuários do tenant';

  return formatCount(totalEligible);
};

/**
 * Why the eligible audience is the number it is, read off **this survey's** registration.
 *
 * The footnote explains the sum; it does not explain the subtraction, and the subtraction is what
 * the number provokes. A survey can show 45 eligible right above an opt-in block reading 96 with
 * the feature on — both correct, because of the 96 only 45 have completed the delay — and nowhere
 * on the screen does it say so. A fixed sentence cannot say it either: which conditions apply is a
 * property of the survey, so the text is assembled from them.
 *
 * Returns lines rather than a paragraph: each line is one condition, and the caller uses the same
 * array for the screen-reader description, so the two can never drift apart.
 */
export const eligibleTooltipLines = ({
  scope,
  totalEligible,
  survey,
  optIn,
}: {
  scope: SurveyEligibilityScope;
  totalEligible: number | null;
  /** Absent while the survey is still loading — the conditions are simply left out. */
  survey?: Survey;
  optIn?: SurveyResultsOptIn;
}): string[] => {
  if (scope === 'EVERYONE' || totalEligible === null) {
    return [
      'Esta pesquisa não tem condição de opt-in: ela alcança todo usuário da plataforma nesta BU.',
      'Não há um número aqui porque o cadastro de quem é usuário da plataforma mora no org backend, não neste serviço. É por isso que a taxa de resposta também aparece como "—": ela não tem denominador.',
    ];
  }

  const lines: string[] = [];
  const featureName = survey?.requiresOptInFeature?.name ?? optIn?.featureName ?? null;
  const state = survey?.requiredOptInState ?? 'ENABLED';
  const enabledNow = optIn?.enabledNow;
  const delayAmount = survey?.delayAmount ?? null;
  const hasDelay = delayAmount !== null && delayAmount > 0;

  // One plain sentence for who gets it, state and waiting time together. Splitting them made the
  // reader assemble the rule from fragments before reaching any number.
  if (featureName) {
    const who = STATE_ECHO[state](featureName);
    const waited = hasDelay
      ? ` há pelo menos ${formatDelay(delayAmount, survey?.delayUnit ?? 'DAYS')}, ${
          ANCHOR_ECHO[survey?.delayAnchor ?? 'LAST_ENABLED_AT']
        }`
      : '';

    lines.push(`Recebe esta pesquisa quem ${who}${waited}.`);
  }

  // The number on its own line, because it is the answer to the question the tooltip was opened
  // with, and it was arriving as an aside at the end of a clause about conditions.
  if (totalEligible !== null) {
    lines.push(`Hoje são ${formatCount(totalEligible)} pessoas.`);
  }

  // The subtraction, and ONLY when there is one to explain. Printing "96 ligados, 96 elegíveis"
  // teaches nothing and reads as a mistake; the gap is the whole reason this tooltip exists.
  if (enabledNow !== undefined && hasDelay && enabledNow !== totalEligible) {
    lines.push(
      `Das ${formatCount(enabledNow)} pessoas com ${featureName ?? 'a funcionalidade'} ligado, ${formatCount(totalEligible)} já esperaram o tempo. As outras entram quando completarem.`,
    );
  }

  if (survey?.maxDaysSinceAnchor) {
    lines.push(`Quem passou de ${formatDelay(survey.maxDaysSinceAnchor, 'DAYS')} sai da conta.`);
  }

  if (survey?.opensAt || survey?.closesAt) {
    lines.push(`A coleta vai de ${formatWindow(survey.opensAt, survey.closesAt)}.`);
  }

  if (survey?.useCase) {
    lines.push(`Só no use case ${survey.useCase.name}.`);
  }

  lines.push(
    'Quem já respondeu continua contando aqui, mesmo que tenha desligado a funcionalidade depois. Sem isso a taxa de resposta poderia passar de 100%.',
  );

  return lines;
};

/**
 * The same rule seen from the denominator's side.
 *
 * The rate is the eligible number under the submitted one, so it hides the same decision; pointing
 * at the card next door is how someone reads it twice and still does not find it.
 */
export const responseRateTooltipLines = ({
  scope,
  totalEligible,
  totalSubmitted,
}: {
  scope: SurveyEligibilityScope;
  totalEligible: number | null;
  totalSubmitted: number;
}): string[] => {
  if (scope === 'EVERYONE' || totalEligible === null) {
    return [
      'Sem denominador: a pesquisa não tem condição de opt-in e alcança todos os usuários da BU, um cadastro que mora no org backend e não neste serviço.',
      'Um "0%" aqui seria invenção, então a taxa aparece como "—" e o público é escrito por extenso ao lado.',
    ];
  }

  return [
    `Pessoas que enviaram ÷ pessoas elegíveis: ${formatCount(totalSubmitted)} ÷ ${formatCount(totalEligible)}.`,
    'Os dois lados contam gente, e é isso que faz a fração ser uma porcentagem: cada pessoa responde uma vez por versão, então "uma resposta" e "uma pessoa" são a mesma coisa aqui.',
    'O denominador é o mesmo do card "Pessoas elegíveis", que detalha de onde ele sai — quem está elegível hoje somado a quem já respondeu, cada pessoa contada uma vez.',
  ];
};

export const eligibleFootnote = (scope: SurveyEligibilityScope): string =>
  scope === 'EVERYONE'
    ? 'A pesquisa não tem condição de opt-in. Quem é usuário da plataforma está no org backend, não neste serviço — por isso não há um número aqui.'
    : 'Quem respondeu, somado a quem está elegível agora — cada pessoa contada uma vez.';

/** `null` is not zero: it is "esta pesquisa não tem denominador", and the screen says so. */
export const responseRateLabel = (scope: SurveyEligibilityScope, rate: number | null): string =>
  scope === 'EVERYONE' || rate === null ? '—' : formatPercent(rate);

export const responseRateFootnote = (scope: SurveyEligibilityScope): string =>
  scope === 'EVERYONE'
    ? 'Sem denominador: a pesquisa alcança todos os usuários do tenant.'
    : 'Pessoas que enviaram sobre pessoas elegíveis.';

/* ─────────────────────── A regra de elegibilidade, em palavras ─────────────────────── */

/**
 * The clauses the eligibility rule is written with.
 *
 * They are phrased to sit inside one sentence — "quem estiver **com a funcionalidade ligada** em
 * `mdm-menu` há pelo menos 7 dias, contados **desde a última vez que ligou**" — rather than to
 * label a box. A field label and a clause of a sentence are not the same string, and using one as
 * the other is what makes a rule builder read like a form.
 */
export const ELIGIBILITY_STATE_CLAUSES: Record<OptInState, string> = {
  ENABLED: 'com a funcionalidade ligada',
  DISABLED: 'com a funcionalidade desligada',
  ANY: 'em qualquer estado dela',
};

export const ELIGIBILITY_ANCHOR_CLAUSES: Record<DelayAnchor, string> = {
  LAST_ENABLED_AT: 'desde a última vez que ligou',
  FIRST_ENABLED_AT: 'desde a primeira vez que ligou',
  DISABLED_AT: 'desde que desligou',
};

/** The unit named as it is read inside the rule, singular and plural. */
export const DELAY_UNIT_WORDS: Record<DelayUnit, { one: string; many: string }> = {
  MINUTES: { one: 'minuto', many: 'minutos' },
  HOURS: { one: 'hora', many: 'horas' },
  DAYS: { one: 'dia', many: 'dias' },
};

/** Options of the unit select, in the order someone reaches for them. */
export const ELIGIBILITY_UNIT_CLAUSES: Record<DelayUnit, string> = {
  DAYS: 'dias',
  HOURS: 'horas',
  MINUTES: 'minutos',
};

/**
 * "7 dias", "1 hora", "5 minutos" — the amount with its unit, never the unit assumed.
 *
 * A delay printed as days when it was registered in minutes is the one mistake this field exists to
 * prevent, so every place that writes a delay goes through here.
 */
export const formatDelay = (amount: number, unit: DelayUnit): string => {
  const words = DELAY_UNIT_WORDS[unit];

  return `${amount} ${amount === 1 ? words.one : words.many}`;
};

export interface EligibilitySentenceInput {
  /** Whether the survey hangs off an opt-in feature at all. */
  conditionOnOptIn: boolean;
  /** The feature's human name, or `null` while none is chosen. */
  featureName: string | null;
  state: OptInState;
  /** As typed: the field is a string, and an empty one is not zero. */
  delayAmount: string;
  delayUnit: DelayUnit;
  anchor: DelayAnchor;
  maxDaysSinceAnchor: string;
  /** The use case the survey is narrowed to, when there is one. */
  useCaseName?: string | null;
}

const STATE_ECHO: Record<OptInState, (feature: string) => string> = {
  ENABLED: feature => `está com ${feature} ligado`,
  DISABLED: feature => `desligou ${feature}`,
  ANY: feature => `já ligou ${feature} alguma vez`,
};

const ANCHOR_ECHO: Record<DelayAnchor, string> = {
  LAST_ENABLED_AT: 'contando desde a última ativação',
  FIRST_ENABLED_AT: 'contando desde a primeira ativação',
  DISABLED_AT: 'contando desde a desativação',
};

/**
 * The rule read back in plain Portuguese, under the sentence that configures it.
 *
 * Three selects can each be read correctly and still add up to something nobody meant — picking
 * "desligada" instead of "ligada" changes who answers and produces a perfectly valid survey aimed
 * at the wrong half of the audience. The echo is where that is caught, before saving.
 */
export const eligibilitySentence = ({
  conditionOnOptIn,
  featureName,
  state,
  delayAmount,
  delayUnit,
  anchor,
  maxDaysSinceAnchor,
  useCaseName,
}: EligibilitySentenceInput): string => {
  if (!conditionOnOptIn) {
    return useCaseName
      ? `Hoje: todo usuário da plataforma nesta BU que for do use case ${useCaseName}.`
      : 'Hoje: todo usuário da plataforma nesta BU.';
  }

  if (!featureName) {
    return 'Escolha a funcionalidade para ver, em uma frase, quem vai receber a pesquisa.';
  }

  const amount = Number(delayAmount.trim());
  const hasDelay = delayAmount.trim() !== '' && Number.isFinite(amount) && amount > 0;
  const period = hasDelay ? ` há ${formatDelay(amount, delayUnit)} ou mais` : '';
  const anchorClause = hasDelay ? `, ${ANCHOR_ECHO[anchor]}` : '';

  const cap = Number(maxDaysSinceAnchor.trim());
  const hasCap = maxDaysSinceAnchor.trim() !== '' && Number.isFinite(cap) && cap > 0;
  const capClause = hasCap ? ` e no máximo ${cap} ${cap === 1 ? 'dia' : 'dias'}` : '';

  // The use case belongs in this line or nowhere: it is the other half of who receives the
  // survey, and left out it is exactly the segmentation nobody notices they configured.
  const useCaseClause = useCaseName ? ` — só no use case ${useCaseName}` : '';

  return `Hoje: quem ${STATE_ECHO[state](featureName)}${period}${capClause}${anchorClause}${useCaseClause}.`;
};

/**
 * The NPS headline.
 *
 * It runs from −100 to 100 and is not a percentage, so it is printed bare: writing "31%" next to
 * a rate that *is* a percentage is how an NPS gets read as "31% gostaram".
 */
export const formatNpsScore = (score: number | null | undefined): string =>
  score === null || score === undefined ? '—' : String(Math.round(score));

/**
 * Where a grid row's average sits on its own scale.
 *
 * The average is a **position** (first option = 1), so it only means something next to the option
 * it lands on: "3,4" alone says nothing, "3,4 — entre Bom e Ótimo" says what was answered.
 */
export const matrixAverageLabel = (
  average: number | null,
  scale: { value: string; label: string }[] | undefined,
): string => {
  if (average === null) return '—';
  if (!scale || scale.length === 0) return formatAverage(average);

  const lower = scale[Math.max(0, Math.min(scale.length - 1, Math.floor(average) - 1))];
  const upper = scale[Math.max(0, Math.min(scale.length - 1, Math.ceil(average) - 1))];

  if (lower === upper) return `${formatAverage(average)} — ${lower.label}`;

  return `${formatAverage(average)} — entre ${lower.label} e ${upper.label}`;
};

/** `0` renders, `undefined` does not — the card shows a dash rather than claiming nobody answered. */
export const formatOptionalCount = (count: number | undefined): string =>
  count === undefined ? '—' : formatCount(count);

/**
 * The version line of a survey under edit.
 *
 * Says out loud that the live version keeps being delivered while a draft is open (R4): the
 * builder looks identical in both cases, and "editar" reads as "tirar do ar" to most people.
 */
export const versionNotice = (survey: Survey): string | null => {
  if (survey.draftVersion === null) return null;

  return `Você está editando a versão ${survey.draftVersion}, em rascunho. A versão ${survey.version} continua no ar e recebendo respostas até esta ser aprovada.`;
};

/** What the trigger field is allowed to do, and the sentence that says why. */
export interface TriggerFieldState {
  /** `true` when the `<select>` is disabled. */
  isLocked: boolean;
  /** What to print beside the field. `null` only while the survey is being created. */
  notice: string | null;
}

/**
 * Whether the trigger can still be changed on a saved survey, and what to say about it (R44).
 *
 * The field used to be disabled on **every** saved survey, which hid a freedom the BFF actually
 * grants: with no response collected there is no data to reinterpret, so swapping the trigger is
 * harmless — and somebody who picked the wrong one on a survey created minutes ago had to delete it
 * and start over. The refusal only exists once somebody has been asked something under the current
 * trigger.
 *
 * **The count is the survey's own `responseCount`: every version, every status** — the same figure
 * the purge dialog states, and the same line the BFF draws. Taking it from the version being edited
 * would understate it on a survey that has been bumped: the answers sitting on v1 do not show on
 * this screen's cards, and unlocking the field against the smaller number would hand the person a
 * `409` after telling them they could.
 *
 * **Locked is never mute.** A disabled field with no explanation sends somebody hunting for the
 * condition that unlocks it, and here the condition is countable — so it is printed. The exit-draft
 * case says something else entirely: nothing unlocks it, because the trigger is the reason that form
 * was opened at all.
 */
export const triggerFieldState = (
  survey: Pick<Survey, 'responseCount'> | undefined,
  isExitDraft: boolean,
): TriggerFieldState => {
  if (isExitDraft) {
    return {
      isLocked: true,
      notice:
        'Este formulário foi aberto como questionário de saída de uma funcionalidade opt-in, e o ' +
        'gatilho é o motivo de ele existir: ele só é perguntado a quem está desligando a ' +
        'funcionalidade. Por isso não se escolhe aqui.',
    };
  }

  if (!survey) return { isLocked: false, notice: null };

  if (survey.responseCount > 0) {
    const answers =
      survey.responseCount === 1 ? '1 resposta' : `${formatCount(survey.responseCount)} respostas`;

    return {
      isLocked: true,
      notice:
        `Travado porque a pesquisa já tem ${answers}, somando todas as versões. O gatilho decide ` +
        'quem é perguntado e quando, então trocá-lo faria essas respostas passarem a viver sob uma ' +
        'pesquisa que pergunta a outro público, em outro momento — e o BFF recusa. Para usar outro ' +
        'gatilho, crie uma pesquisa nova; se as respostas forem de teste, resete esta antes.',
    };
  }

  return {
    isLocked: false,
    notice:
      'Ainda dá para trocar: sem nenhuma resposta coletada, não há dado para reinterpretar. A ' +
      'partir da primeira resposta, o gatilho trava.',
  };
};

/**
 * Whether pressing "Salvar alterações" is about to open the **next** version (R4).
 *
 * The BFF opens a draft when three things hold at once (`saveQuestions`, `opensDraft`): the version
 * being edited has **no** responses, the survey is `APPROVED`, and no draft is open yet. It is not
 * about what changed — an approved survey in that state opens version N+1 even if the save carries
 * no edit at all, which is how somebody changes one letter and lands in "v2 aguardando aprovação"
 * without ever being told.
 *
 * **The response count has to be the one of the version being edited, not the survey's.**
 * `survey.responseCount` counts every version, and the two disagree in both directions: a survey
 * delivering v2 with its 309 answers sitting on v1 opens a draft (total > 0, this version 0), and a
 * survey whose delivered version holds the only answer does not (R37 decides that save instead —
 * in place when cosmetic, `409` otherwise). Warning in that second case would promise a version
 * that never opens.
 *
 * `versionResponseCount` is `null` while that count is still unknown — the request is in flight, or
 * it failed. **Unknown warns.** Being told about a version that then does not open costs one
 * cancelled dialog; not being told is the bug this exists to close.
 */
export const savingOpensNextVersion = (
  survey: Pick<Survey, 'approvalStatus' | 'draftVersion' | 'responseCount'>,
  versionResponseCount: number | null,
): boolean => {
  if (survey.approvalStatus !== 'APPROVED') return false;
  if (survey.draftVersion !== null) return false;
  // No answer anywhere means no answer in this version either — no request needed to know it.
  if (survey.responseCount === 0) return true;

  return versionResponseCount === null || versionResponseCount === 0;
};

/** How far a step of the funnel got, as a share of the first step. */
export const funnelShare = (reached: number, firstStepReached: number): number =>
  firstStepReached > 0 ? reached / firstStepReached : 0;

/** `en` → `Inglês`, for the comment feed, where the language is data and not a tab. */
export const languageName = (language: string | null): string => {
  const names: Record<string, string> = { en: 'Inglês', pt: 'Português', es: 'Espanhol' };

  return language ? (names[language] ?? language) : '—';
};

export const COMMENT_SOURCE_LABELS: Record<'ANSWER' | 'OTHER_TEXT', string> = {
  ANSWER: 'Pergunta aberta',
  OTHER_TEXT: 'Campo "Outro"',
};

/* ────────────────────────── Formas dos gráficos do resultado ────────────────────── */

/**
 * Where a grid row's average sits, as a fraction of the bar.
 *
 * The average is a **position on the scale**, first option = 1, so on a four-level scale a 3.4
 * belongs at (3.4 − 1) / (4 − 1) ≈ 0.8 of the way across. `null` when there is no average — a
 * BOOLEAN row, where sim and não have no order to average.
 */
export const scaleMarkerPosition = (average: number | null, scaleLength: number): number | null => {
  if (average === null || scaleLength < 2) return null;

  return Math.min(Math.max((average - 1) / (scaleLength - 1), 0), 1);
};

/** The three NPS buckets, in the order they are read. Semantic colour: here it means something. */
export const npsSegments = (
  question: Pick<SurveyQuestionResult, 'detractors' | 'passives' | 'promoters'>,
): { label: string; value: number; color: string }[] => [
  { label: 'Detratores (0–6)', value: question.detractors ?? 0, color: 'var(--color-danger)' },
  { label: 'Neutros (7–8)', value: question.passives ?? 0, color: 'var(--color-border-strong)' },
  { label: 'Promotores (9–10)', value: question.promoters ?? 0, color: 'var(--color-success)' },
];

/**
 * The one number a question card leads with, or `null` when it has none.
 *
 * It is also what the collapsed "Perguntas" section summarises: a section nobody can see into is
 * only worth opening if the headline says something, and "8 perguntas" is the one thing the reader
 * already knows.
 */
export const questionHeadline = (question: SurveyQuestionResult): string | null => {
  if (question.type === 'RATING' && question.average !== null && question.average !== undefined) {
    return `${formatAverage(question.average)} de média`;
  }

  if (question.type === 'NPS' && question.score !== null && question.score !== undefined) {
    return `NPS ${formatNpsScore(question.score)}`;
  }

  if (question.type === 'BOOLEAN' && question.truePercent !== undefined) {
    return `${formatPercent(question.truePercent)} sim`;
  }

  return null;
};

/**
 * What the collapsed "Perguntas" section says.
 *
 * The headline numbers that exist, in question order. Only when a survey has none of them — all
 * choice and text questions — does it fall back to counting, which is the least useful thing it
 * could say and therefore the last resort.
 */
export const questionsSummary = (questions: SurveyQuestionResult[]): string => {
  const headlines = questions
    .map(question => questionHeadline(question))
    .filter((headline): headline is string => headline !== null);

  if (headlines.length > 0) return headlines.join(' · ');
  if (questions.length === 0) return 'sem respostas nesta versão';

  return `${questions.length} ${questions.length === 1 ? 'pergunta' : 'perguntas'}`;
};

/**
 * What the collapsed funnel says: the rate and the two numbers behind it.
 *
 * Both numbers are people (R22b), and the summary says so — collapsed, this line is the only label
 * those two numbers have.
 */
export const funnelSummary = (results: SurveyResults): string => {
  const rate = responseRateLabel(results.eligibilityScope, results.responseRate);
  const submitted = formatCount(results.totalSubmitted);

  if (results.eligibilityScope === 'EVERYONE' || results.totalEligible === null) {
    return `${submitted} pessoas enviaram · público sem denominador`;
  }

  return `${rate} · ${submitted} de ${formatCount(results.totalEligible)} pessoas`;
};

/** What the collapsed opt-in block says: who is on it now, and how many gave up. */
export const optInSummary = (optIn: SurveyResultsOptIn): string =>
  `${formatCount(optIn.enabledNow)} com a funcionalidade ligada · ${formatPercent(optIn.revertRate)} voltaram atrás`;

/** A count that says what it is when there is nothing, instead of printing a bare zero. */
export const countSummary = (total: number, singular: string, plural: string): string => {
  if (total === 0) return `nenhum${singular.endsWith('a') ? 'a' : ''} ${singular}`;

  return `${formatCount(total)} ${total === 1 ? singular : plural}`;
};
